import { useRef, useState } from "react";
import { VoiceBars } from "./VoiceBars";

// Voice-bars box: 18% of the video width, square. Default spot is the top-right corner.
export const BARS_W = 0.18;
export const DEFAULT_BARS_POS = { x: 1 - BARS_W - 0.02, y: 0.14 };
const clamp = (v, max) => Math.min(Math.max(v, 0), max);

// The 16:9 source stage. The voice-bars box shows only while a comment is spoken ("bars"),
// as a faint draggable outline while paused in the editor ("outline"), and is hidden otherwise.
export function VoiceStage({ stageRef, playerHostRef, barsPos, onBarsMoved, mode, locked, countdown }) {
  const [drag, setDrag] = useState(null);
  const dragRef = useRef(null);
  const pos = drag || barsPos;
  const movable = !locked && mode !== "hidden";

  const down = (e) => {
    if (!movable) return;
    e.preventDefault();
    const box = e.currentTarget.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - box.left, dy: e.clientY - box.top };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag(barsPos);
  };
  const move = (e) => {
    if (!dragRef.current) return;
    const st = stageRef.current.getBoundingClientRect();
    const hFrac = (BARS_W * st.width) / st.height;
    setDrag({
      x: clamp((e.clientX - dragRef.current.dx - st.left) / st.width, 1 - BARS_W),
      y: clamp((e.clientY - dragRef.current.dy - st.top) / st.height, 1 - hFrac),
    });
  };
  const up = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (drag) onBarsMoved(drag);
    setDrag(null);
  };

  return (
    <div ref={stageRef} className="relative w-full bg-black rounded-[4px] overflow-hidden" style={{ aspectRatio: "16 / 9" }} data-testid="video-stage">
      <div ref={playerHostRef} className="absolute inset-0 w-full h-full" />
      {(locked || drag) && <div className="absolute inset-0 z-10" data-testid="stage-lock-overlay" />}
      <div data-testid="voice-bars-overlay" data-mode={mode} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
        title={movable ? "Drag to move" : undefined}
        className={`absolute z-20 rounded-md touch-none select-none transition-[opacity,border-color] duration-200 ${mode === "hidden" ? "opacity-0 pointer-events-none" : "opacity-100"} ${mode === "outline" ? "border-2 border-dashed border-white/60 bg-black/10" : "border-2 border-yellow-400 shadow-xl"} ${movable ? (drag ? "cursor-grabbing" : "cursor-grab") : ""}`}
        style={{ left: `${pos.x * 100}%`, top: `${pos.y * 100}%`, width: `${BARS_W * 100}%`, aspectRatio: "1 / 1" }}>
        <div className={`w-full h-full overflow-hidden rounded-[4px] transition-opacity duration-200 ${mode === "bars" ? "opacity-100" : "opacity-0"}`}><VoiceBars /></div>
        {mode === "outline" && (
          <span className="absolute inset-0 flex items-center justify-center text-center text-white/80 font-medium leading-tight px-2" style={{ fontSize: "clamp(8px, 0.7vw, 12px)" }}
            data-testid="voice-bars-outline-hint">Voice box<br />drag to move</span>
        )}
      </div>
      {countdown !== null && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/50" data-testid="countdown-overlay">
          <span className="text-white font-display font-bold text-8xl tabular-nums">{countdown}</span>
        </div>
      )}
    </div>
  );
}
